import { useState, useRef, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
    MessageCircle,
    X,
    Send,
    Bot,
    User,
    Sparkles,
    Zap,
    FileText,
    HelpCircle
} from 'lucide-react';

export default function Chatbot() {
    const [isOpen, setIsOpen] = useState(false);
    const [messages, setMessages] = useState([
        {
            id: 1,
            type: 'bot',
            text: "👋 Hi! I'm your BlinqBooks Support Assistant. I'm here to help with your questions and connect you with our team for business enquiries!",
            timestamp: new Date()
        }
    ]);
    const [inputValue, setInputValue] = useState('');
    const [isTyping, setIsTyping] = useState(false);
    const messagesEndRef = useRef(null);
    const inputRef = useRef(null);

    const quickActions = [
        { icon: <MessageCircle className="w-4 h-4" />, text: "Contact Support", color: "blue" },
        { icon: <Sparkles className="w-4 h-4" />, text: "Business Enquiry", color: "purple" },
        { icon: <FileText className="w-4 h-4" />, text: "Request Demo", color: "green" },
        { icon: <HelpCircle className="w-4 h-4" />, text: "Get Help", color: "pink" }
    ];

    const scrollToBottom = () => {
        messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
    };

    useEffect(() => {
        scrollToBottom();
    }, [messages]);

    useEffect(() => {
        if (isOpen && inputRef.current) {
            inputRef.current.focus();
        }
    }, [isOpen]);

    const handleSendMessage = async (messageText = inputValue) => {
        if (!messageText.trim()) return;

        const userMessage = {
            id: Date.now(),
            type: 'user',
            text: messageText,
            timestamp: new Date()
        };

        setMessages(prev => [...prev, userMessage]);
        setInputValue('');
        setIsTyping(true);

        // Simulate AI response
        setTimeout(() => {
            const botResponse = generateBotResponse(messageText);
            setMessages(prev => [...prev, {
                id: Date.now() + 1,
                type: 'bot',
                text: botResponse,
                timestamp: new Date()
            }]);
            setIsTyping(false);
        }, 1000 + Math.random() * 1000);
    };

    const generateBotResponse = (userMessage) => {
        const lowerMessage = userMessage.toLowerCase();

        // Contact Support
        if (lowerMessage.includes('contact') || lowerMessage.includes('support') || lowerMessage.includes('help')) {
            return "I'd be happy to connect you with our support team! 📧\n\nYou can reach us at:\n• Email: support@blinqbooks.com\n• Phone: +1 (800) 123-4567\n• Live Chat: Available 24/7\n\nOr tell me about your issue and I'll help route it to the right team!";
        }
        // Business Enquiry
        else if (lowerMessage.includes('business') || lowerMessage.includes('enquiry') || lowerMessage.includes('inquiry') || lowerMessage.includes('enterprise') || lowerMessage.includes('partnership')) {
            return "Great to hear you're interested in BlinqBooks for your business! 🚀\n\nOur business team would love to discuss:\n• Custom enterprise solutions\n• Volume pricing\n• Integration support\n• Dedicated account management\n\nEmail: business@blinqbooks.com\nPhone: +1 (800) 123-BLINQ\n\nWould you like to schedule a call?";
        }
        // Demo Request
        else if (lowerMessage.includes('demo') || lowerMessage.includes('demonstration') || lowerMessage.includes('trial')) {
            return "Excellent! I can help you schedule a personalized demo! 🎯\n\nOur product specialists will show you:\n✓ Complete platform walkthrough\n✓ Custom features for your industry\n✓ Integration capabilities\n✓ ROI analysis\n\nPlease share your:\n• Company name\n• Email address\n• Preferred date/time\n\nOr visit: demo.blinqbooks.com";
        }
        // Sales & Pricing
        else if (lowerMessage.includes('price') || lowerMessage.includes('pricing') || lowerMessage.includes('cost') || lowerMessage.includes('plan') || lowerMessage.includes('sales')) {
            return "I can help you find the perfect plan! 💰\n\nOur pricing is designed for businesses of all sizes:\n• Starter: $29/month - Perfect for small businesses\n• Professional: $99/month - Growing teams\n• Enterprise: Custom pricing - Large organizations\n\nAll plans include:\n✓ 14-day free trial\n✓ No credit card required\n✓ Cancel anytime\n\nWant to speak with our sales team? Email: sales@blinqbooks.com";
        }
        // Technical Issues
        else if (lowerMessage.includes('bug') || lowerMessage.includes('error') || lowerMessage.includes('issue') || lowerMessage.includes('problem') || lowerMessage.includes('not working')) {
            return "I'm sorry you're experiencing an issue! 🔧\n\nOur technical support team is here to help:\n• Email: tech@blinqbooks.com\n• Priority Support: Available for Pro+ plans\n• Response time: Within 2 hours\n\nPlease describe your issue and we'll get it resolved quickly!";
        }
        // Features & Product Info
        else if (lowerMessage.includes('feature') || lowerMessage.includes('what') || lowerMessage.includes('how')) {
            return "BlinqBooks is your all-in-one business platform! 🌟\n\nKey features:\n• Accounting & GST compliance\n• Expense tracking with AI\n• Website builder\n• Social media management\n• POS system\n• Payroll automation\n\nWant to learn more about a specific feature? I can connect you with our product team!";
        }
        // Greetings
        else if (lowerMessage.includes('hi') || lowerMessage.includes('hello') || lowerMessage.includes('hey')) {
            return "Hello! 👋 Welcome to BlinqBooks support!\n\nI can help you with:\n• Contacting our support team\n• Business enquiries\n• Scheduling a demo\n• Pricing information\n• Technical assistance\n\nWhat can I help you with today?";
        }
        // Default Response
        else {
            return "Thanks for reaching out! 😊\n\nI'm here to help you connect with the right team:\n• Support issues → support@blinqbooks.com\n• Business enquiries → business@blinqbooks.com\n• Sales questions → sales@blinqbooks.com\n\nOr you can call us at +1 (800) 123-4567\n\nHow else can I assist you?";
        }
    };

    const handleQuickAction = (action) => {
        handleSendMessage(action);
    };

    const handleKeyPress = (e) => {
        if (e.key === 'Enter' && !e.shiftKey) {
            e.preventDefault();
            handleSendMessage();
        }
    };

    return (
        <>
            {/* Chatbot Toggle Button */}
            <AnimatePresence>
                {!isOpen && (
                    <motion.button
                        initial={{ scale: 0, opacity: 0 }}
                        animate={{ scale: 1, opacity: 1 }}
                        exit={{ scale: 0, opacity: 0 }}
                        whileHover={{ scale: 1.1 }}
                        whileTap={{ scale: 0.9 }}
                        onClick={() => setIsOpen(true)}
                        className="fixed bottom-4 right-4 sm:bottom-6 sm:right-6 z-50 w-14 h-14 sm:w-16 sm:h-16 rounded-full bg-gradient-to-br from-blue-500 via-purple-500 to-pink-500 text-white shadow-2xl hover:shadow-purple-500/50 transition-all duration-300 flex items-center justify-center group"
                        aria-label="Open BlinqBot AI Assistant"
                    >
                        <MessageCircle className="w-6 h-6 sm:w-7 sm:h-7 group-hover:scale-110 transition-transform" />

                        {/* Pulse Animation */}
                        <span className="absolute inset-0 rounded-full bg-gradient-to-br from-blue-500 via-purple-500 to-pink-500 animate-ping opacity-20 pointer-events-none"></span>

                        {/* Sparkle Effect */}
                        <motion.div
                            className="absolute -top-1 -right-1"
                            animate={{
                                scale: [1, 1.2, 1],
                                rotate: [0, 180, 360]
                            }}
                            transition={{
                                duration: 2,
                                repeat: Infinity,
                                ease: "easeInOut"
                            }}
                        >
                            <Sparkles className="w-4 h-4 sm:w-5 sm:h-5 text-yellow-300" />
                        </motion.div>
                    </motion.button>
                )}
            </AnimatePresence>

            {/* Chatbot Window */}
            <AnimatePresence>
                {isOpen && (
                    <motion.div
                        initial={{ opacity: 0, y: 50, scale: 0.95 }}
                        animate={{ opacity: 1, y: 0, scale: 1 }}
                        exit={{ opacity: 0, y: 50, scale: 0.95 }}
                        transition={{ type: "spring", damping: 25, stiffness: 300 }}
                        className="fixed inset-x-2 bottom-2 sm:inset-auto sm:bottom-6 sm:right-6 z-50 w-auto sm:w-[400px] h-[82vh] sm:h-[600px] max-h-[600px] flex flex-col bg-white dark:bg-gray-900 rounded-2xl sm:rounded-3xl shadow-2xl border border-gray-200 dark:border-gray-700 overflow-hidden"
                    >
                        {/* Header */}
                        <div className="relative bg-gradient-to-br from-blue-500 via-purple-500 to-pink-500 p-6 text-white">
                            {/* Animated Background Pattern */}
                            <div className="absolute inset-0 opacity-20">
                                {[...Array(20)].map((_, i) => (
                                    <motion.div
                                        key={i}
                                        className="absolute bg-white rounded-full"
                                        style={{
                                            width: Math.random() * 4 + 2 + 'px',
                                            height: Math.random() * 4 + 2 + 'px',
                                            top: Math.random() * 100 + '%',
                                            left: Math.random() * 100 + '%',
                                        }}
                                        animate={{
                                            y: [0, -30, 0],
                                            opacity: [0, 1, 0],
                                        }}
                                        transition={{
                                            duration: Math.random() * 3 + 2,
                                            repeat: Infinity,
                                            ease: "easeInOut",
                                            delay: Math.random() * 2,
                                        }}
                                    />
                                ))}
                            </div>

                            <div className="relative flex items-center justify-between">
                                <div className="flex items-center gap-3">
                                    <motion.div
                                        animate={{
                                            rotate: [0, 360],
                                        }}
                                        transition={{
                                            duration: 20,
                                            repeat: Infinity,
                                            ease: "linear"
                                        }}
                                        className="w-12 h-12 rounded-full bg-white/20 backdrop-blur-sm flex items-center justify-center border-2 border-white/30"
                                    >
                                        <Bot className="w-6 h-6" />
                                    </motion.div>
                                    <div>
                                        <h3 className="font-bold text-lg">BlinqBot</h3>
                                        <div className="flex items-center gap-1.5">
                                            <motion.div
                                                animate={{
                                                    scale: [1, 1.2, 1],
                                                }}
                                                transition={{
                                                    duration: 2,
                                                    repeat: Infinity,
                                                }}
                                                className="w-2 h-2 rounded-full bg-green-400"
                                            />
                                            <span className="text-xs text-white/90">Support & Business Enquiries</span>
                                        </div>
                                    </div>
                                </div>
                                <motion.button
                                    whileHover={{ scale: 1.1, rotate: 90 }}
                                    whileTap={{ scale: 0.9 }}
                                    onClick={() => setIsOpen(false)}
                                    className="w-8 h-8 rounded-full bg-white/20 hover:bg-white/30 backdrop-blur-sm flex items-center justify-center transition-colors"
                                >
                                    <X className="w-5 h-5" />
                                </motion.button>
                            </div>
                        </div>

                        {/* Messages Area */}
                        <div className="flex-1 overflow-y-auto p-4 space-y-4 bg-gray-50 dark:bg-gray-950">
                            {messages.map((message, index) => (
                                <motion.div
                                    key={message.id}
                                    initial={{ opacity: 0, y: 20 }}
                                    animate={{ opacity: 1, y: 0 }}
                                    transition={{ delay: index * 0.1 }}
                                    className={`flex gap-3 ${message.type === 'user' ? 'flex-row-reverse' : 'flex-row'}`}
                                >
                                    {/* Avatar */}
                                    <div className={`flex-shrink-0 w-8 h-8 rounded-full flex items-center justify-center ${message.type === 'bot'
                                        ? 'bg-gradient-to-br from-blue-500 to-purple-500 text-white'
                                        : 'bg-gray-300 dark:bg-gray-700 text-gray-700 dark:text-gray-300'
                                        }`}>
                                        {message.type === 'bot' ? <Bot className="w-4 h-4" /> : <User className="w-4 h-4" />}
                                    </div>

                                    {/* Message Bubble */}
                                    <div className={`max-w-[75%] rounded-2xl px-4 py-3 ${message.type === 'bot'
                                        ? 'bg-white dark:bg-gray-800 text-gray-800 dark:text-gray-200 shadow-sm border border-gray-200 dark:border-gray-700'
                                        : 'bg-gradient-to-br from-blue-500 to-purple-500 text-white shadow-md'
                                        }`}>
                                        <p className="text-sm leading-relaxed whitespace-pre-line">{message.text}</p>
                                        <span className={`text-xs mt-1 block ${message.type === 'bot' ? 'text-gray-400' : 'text-white/70'
                                            }`}>
                                            {message.timestamp.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                                        </span>
                                    </div>
                                </motion.div>
                            ))}

                            {/* Typing Indicator */}
                            {isTyping && (
                                <motion.div
                                    initial={{ opacity: 0, y: 20 }}
                                    animate={{ opacity: 1, y: 0 }}
                                    className="flex gap-3"
                                >
                                    <div className="w-8 h-8 rounded-full bg-gradient-to-br from-blue-500 to-purple-500 text-white flex items-center justify-center">
                                        <Bot className="w-4 h-4" />
                                    </div>
                                    <div className="bg-white dark:bg-gray-800 rounded-2xl px-4 py-3 shadow-sm border border-gray-200 dark:border-gray-700">
                                        <div className="flex gap-1">
                                            {[0, 1, 2].map((i) => (
                                                <motion.div
                                                    key={i}
                                                    className="w-2 h-2 rounded-full bg-gray-400"
                                                    animate={{
                                                        y: [0, -8, 0],
                                                    }}
                                                    transition={{
                                                        duration: 0.6,
                                                        repeat: Infinity,
                                                        delay: i * 0.1,
                                                    }}
                                                />
                                            ))}
                                        </div>
                                    </div>
                                </motion.div>
                            )}

                            <div ref={messagesEndRef} />
                        </div>

                        {/* Quick Actions */}
                        {messages.length <= 2 && (
                            <div className="px-4 py-3 bg-white dark:bg-gray-900 border-t border-gray-200 dark:border-gray-700">
                                <p className="text-xs text-gray-500 dark:text-gray-400 mb-2 font-medium">Quick Actions</p>
                                <div className="grid grid-cols-2 gap-2">
                                    {quickActions.map((action, index) => (
                                        <motion.button
                                            key={index}
                                            whileHover={{ scale: 1.02 }}
                                            whileTap={{ scale: 0.98 }}
                                            onClick={() => handleQuickAction(action.text)}
                                            className={`flex items-center gap-2 px-3 py-2 rounded-xl text-xs font-medium transition-all bg-${action.color}-50 dark:bg-${action.color}-900/20 text-${action.color}-600 dark:text-${action.color}-400 hover:bg-${action.color}-100 dark:hover:bg-${action.color}-900/30 border border-${action.color}-200 dark:border-${action.color}-800`}
                                        >
                                            {action.icon}
                                            <span className="truncate">{action.text}</span>
                                        </motion.button>
                                    ))}
                                </div>
                            </div>
                        )}

                        {/* Input Area */}
                        <div className="p-4 bg-white dark:bg-gray-900 border-t border-gray-200 dark:border-gray-700">
                            <div className="flex gap-2">
                                <div className="flex-1 relative">
                                    <input
                                        ref={inputRef}
                                        type="text"
                                        value={inputValue}
                                        onChange={(e) => setInputValue(e.target.value)}
                                        onKeyPress={handleKeyPress}
                                        placeholder="Type your message..."
                                        className="w-full px-4 py-3 pr-10 rounded-2xl bg-gray-100 dark:bg-gray-800 border border-gray-200 dark:border-gray-700 focus:outline-none focus:ring-2 focus:ring-purple-500 dark:focus:ring-purple-400 text-gray-800 dark:text-gray-200 placeholder-gray-400 dark:placeholder-gray-500 transition-all"
                                    />
                                    <Zap className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                                </div>
                                <motion.button
                                    whileHover={{ scale: 1.05 }}
                                    whileTap={{ scale: 0.95 }}
                                    onClick={() => handleSendMessage()}
                                    disabled={!inputValue.trim()}
                                    className="w-12 h-12 rounded-2xl bg-gradient-to-br from-blue-500 to-purple-500 text-white flex items-center justify-center shadow-lg hover:shadow-purple-500/50 transition-all disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:scale-100"
                                >
                                    <Send className="w-5 h-5" />
                                </motion.button>
                            </div>
                            <p className="text-xs text-gray-400 dark:text-gray-500 mt-2 text-center">
                                Powered by BlinqBooks AI
                            </p>
                        </div>
                    </motion.div>
                )}
            </AnimatePresence>
        </>
    );
}
